// Barra de "actualizar datos" del dashboard (ticket 7).
//
// La ingesta NO corre sola: Cloudflare Pages no soporta cron triggers, solo los Workers.
// Charly eligió el botón manual antes que montar un Worker aparte solo para dispararlo.
// Por eso esta barra tiene que decir SIEMPRE de cuándo son los datos: si nadie aprieta el
// botón, la app sigue mostrando lo de la última corrida y nada avisaría que está vieja.
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui';
import { useIngestStatus, useRunIngest } from '@/lib/hooks';
import { cn } from '@/lib/cn';
import { formatNumber } from '@shared/format';

export function UpdateDataPanel() {
  const estado = useIngestStatus();
  const actualizar = useRunIngest();
  const last = estado.data?.last ?? null;
  const sinB2B = estado.data?.b2bConectado === false;

  return (
    <div className="surface flex flex-wrap items-center justify-between gap-3 p-4">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink-strong">Datos del B2B</p>
        <p className="mt-0.5 text-xs text-ink-muted">
          {estado.isLoading
            ? 'Cargando…'
            : last
              ? `Última actualización: ${cuandoFue(last.finishedAt ?? last.startedAt)}`
              : 'Nunca se actualizó desde esta pantalla.'}
        </p>
        {last?.detail && (
          <p className="mt-1 truncate text-2xs text-ink-faint" title={last.detail}>
            {last.detail}
          </p>
        )}
        {/* Una corrida que quedó marcada como fallida es un dato, no ruido: significa que lo
            que se está mirando arriba puede ser de antes de ese intento. */}
        {last && !last.ok && last.finishedAt && (
          <p className="mt-1 flex items-center gap-1.5 text-2xs text-danger-text">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            La última actualización falló. Los datos son de la corrida anterior.
          </p>
        )}
      </div>

      <div className="flex flex-col items-end gap-1.5">
        <Button
          variant="primary"
          disabled={actualizar.isPending || sinB2B}
          onClick={() => actualizar.mutate()}
          icon={
            actualizar.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )
          }
          title={sinB2B ? 'No hay conexión con el B2B' : 'Trae la venta y el stock del B2B'}
        >
          {actualizar.isPending ? 'Actualizando…' : 'Actualizar datos'}
        </Button>
        <p
          className={cn(
            'text-2xs',
            actualizar.isError ? 'text-danger-text' : actualizar.isSuccess ? 'text-success-text' : 'text-ink-faint',
          )}
        >
          {/* Avisar la demora es parte del diseño: son ~20 s de reloj y sin esto parece colgado. */}
          {actualizar.isPending
            ? 'Puede tardar unos 20 segundos. No cierres la pestaña.'
            : actualizar.isError
              ? (actualizar.error as Error).message
              : actualizar.isSuccess
                ? resumen(actualizar.data)
                : sinB2B
                  ? 'Sin conexión con el B2B.'
                  : 'Trae la venta, el stock y los faltantes del B2B.'}
        </p>
      </div>
    </div>
  );
}

function resumen(r: { ventas: number; productos: number; ms: number } | undefined): string {
  if (!r) return 'Listo.';
  return `✓ ${formatNumber(r.ventas)} filas de venta y ${formatNumber(r.productos)} productos en ${Math.round(r.ms / 1000)} s.`;
}

/** "hace 4 minutos" / "hace 3 horas" / fecha y hora si ya pasó más de un día. */
function cuandoFue(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} minuto${min === 1 ? '' : 's'}`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} hora${h === 1 ? '' : 's'}`;
  return new Date(t).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
